"use client";

/**
 * ResetPasswordModal
 *
 * Top-level modal that implements the "Forgot Password" flow.
 *
 * Flow responsibilities:
 *  - Request an OTP for the provided email (request-password-reset action)
 *  - Show a verify step with an OTP input and progress/resend UI
 *  - Verify OTP (verify-reset-otp action) and allow the user to reset the password
 */
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { DEFAULT_OTP_LENGTH, RESEND_OTP_INTERVAL_SECONDS } from "@/config";
import { useAuth } from "@/hooks/useAuth";
import { useAlert } from "@/Provider/AlertProvider/AlertProvider";
import CModal from "@/Shared/CModal/CModal";
import RequestStep from "./RequestStep";
import Stepper from "./Stepper";
import VerifyStep from "./VerifyStep";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialEmail?: string;
}

const ResetPasswordModal: React.FC<Props> = ({
  isOpen,
  onClose,
  initialEmail,
}) => {
  const { requestPasswordReset, verifyResetOtp, resetPasswordWithOtp } =
    useAuth();
  const { showError } = useAlert();

  const [step, setStep] = useState<"request" | "verify">("request");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  // Initialize / reset state when the modal opens or closes
  useEffect(() => {
    if (isOpen && initialEmail) {
      setEmail(initialEmail);
    }
    if (!isOpen) {
      setStep("request");
      setEmail("");
      setOtp("");
      setOtpVerified(false);
      setNewPassword("");
      setConfirmPassword("");
      setInfoMessage(null);
      setSecondsRemaining(0);
    }
  }, [isOpen, initialEmail]);

  // Start countdown when entering the verify step.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run on step change; secondsRemaining is checked once at the boundary
  useEffect(() => {
    if (step === "verify" && secondsRemaining === 0) {
      setSecondsRemaining(RESEND_OTP_INTERVAL_SECONDS);
    }
  }, [step]);

  // Tick the countdown
  useEffect(() => {
    if (secondsRemaining <= 0) return;
    const timer = setInterval(() => {
      setSecondsRemaining((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsRemaining]);

  /** Step 1: request an OTP */
  const handleRequest = useCallback(async () => {
    setLoading(true);
    try {
      const res = await requestPasswordReset(email);
      const message =
        res.message ||
        "If this email exists, a password reset code has been sent";
      setInfoMessage(message);

      if (res.success) {
        setStep("verify");
        setSecondsRemaining(RESEND_OTP_INTERVAL_SECONDS);
      }
    } finally {
      setLoading(false);
    }
  }, [email, requestPasswordReset]);

  /** Step 1 (resend): only available after the countdown elapses */
  const handleResend = useCallback(async () => {
    if (secondsRemaining > 0) return;
    setLoading(true);
    try {
      const res = await requestPasswordReset(email);
      const message =
        res.message ||
        "If this email exists, a password reset code has been sent";
      setInfoMessage(message);
      setSecondsRemaining(RESEND_OTP_INTERVAL_SECONDS);
      setOtpVerified(false);
    } finally {
      setLoading(false);
    }
  }, [email, requestPasswordReset, secondsRemaining]);

  /** Step 2: auto-verify when the OTP input becomes complete */
  const handleOtpComplete = useCallback(
    async (val: string) => {
      setOtp(val);
      if (!email) {
        showError("Email is required to verify OTP");
        return;
      }
      setLoading(true);
      try {
        const res = await verifyResetOtp(email, val);
        const message =
          res.message || (res.success ? "OTP verified" : "Invalid OTP");
        setInfoMessage(message);
        setOtpVerified(!!res.success);
      } catch (_err) {
        setOtpVerified(false);
        showError("Verification failed");
      } finally {
        setLoading(false);
      }
    },
    [verifyResetOtp, email, showError],
  );

  /** Step 3: finalize the password reset */
  const handleVerifyAndReset = useCallback(async () => {
    // Mirror the backend policy (auth.md §1.7, v0.185): min 8, at least one
    // letter AND one number. Guard here so a valid-length-but-digits-only value
    // (e.g. "12345678") is caught inline instead of coming back as a 400.
    if (
      newPassword.length < 8 ||
      !/[A-Za-z]/.test(newPassword) ||
      !/[0-9]/.test(newPassword)
    ) {
      showError(
        "Password must be at least 8 characters and include a letter and a number",
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      showError("Passwords do not match");
      return;
    }
    if (!otp) {
      showError("OTP is required");
      return;
    }
    setLoading(true);
    try {
      // Skip duplicate verify call if already verified by onOtpComplete
      if (!otpVerified) {
        const verifyRes = await verifyResetOtp(email, otp);
        if (!verifyRes.success) return;
      }
      const resetRes = await resetPasswordWithOtp(email, otp, newPassword);
      if (resetRes.success) {
        onClose();
      }
    } finally {
      setLoading(false);
    }
  }, [
    email,
    otp,
    newPassword,
    confirmPassword,
    verifyResetOtp,
    resetPasswordWithOtp,
    onClose,
    showError,
    otpVerified,
  ]);

  return (
    <CModal
      isOpen={isOpen}
      onClose={onClose}
      title={step === "request" ? "Reset Password" : "Verify OTP"}
    >
      <div className="space-y-4">
        <Stepper active={step} />
        {step === "request" ? (
          <RequestStep
            email={email}
            setEmail={setEmail}
            onRequest={handleRequest}
            loading={loading}
          />
        ) : (
          <VerifyStep
            email={email}
            otp={otp}
            newPassword={newPassword}
            confirmPassword={confirmPassword}
            setOtp={setOtp}
            setNewPassword={setNewPassword}
            setConfirmPassword={setConfirmPassword}
            onVerify={handleVerifyAndReset}
            onOtpComplete={handleOtpComplete}
            onResend={handleResend}
            secondsRemaining={secondsRemaining}
            loading={loading}
            infoMessage={infoMessage}
            onDismissInfo={() => setInfoMessage(null)}
            onChangeEmail={() => {
              setStep("request");
              setOtpVerified(false);
            }}
            otpVerified={otpVerified}
            otpLength={DEFAULT_OTP_LENGTH}
          />
        )}
      </div>
    </CModal>
  );
};

export default ResetPasswordModal;
